import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANG_LABELS, SUPPORTED_LANGS, MAIN_SITE_URL, categories } from '@/data/categories';
import ScrollLink from '@/components/feature/ScrollLink';

export default function Header() {
  const { t } = useTranslation();
  const { lang = 'en' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const switchLang = (newLang: string) => {
    const segments = location.pathname.split('/').filter(Boolean);
    const suffix = segments.slice(1).join('/');
    setLangOpen(false);
    setMobileOpen(false);
    navigate(suffix ? `/${newLang}/${suffix}` : `/${newLang}`);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
      <nav className="mx-auto max-w-7xl px-4 md:px-6 h-16 flex items-center justify-between">
        <Link
          to={`/${lang}`}
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => setMobileOpen(false)}
        >
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-table-line text-lg" />
          </span>
          <span className="font-heading font-extrabold text-lg text-foreground-950">
            {t('logo.name')}
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to={`/${lang}`}
            className="text-sm font-semibold text-foreground-800 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('nav.home')}
          </Link>
          <Link
            to={`/${lang}/guide/basetao-buying-guide`}
            className="text-sm font-semibold text-foreground-800 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('nav.guide')}
          </Link>
          <ScrollLink to="categories">{t('nav.products')}</ScrollLink>
          <ScrollLink to="topics">{t('nav.guides')}</ScrollLink>
          <ScrollLink to="faq">{t('nav.safety')}</ScrollLink>
        </div>

        <div className="flex items-center gap-3">
          <div ref={langRef} className="relative">
            <button
              type="button"
              onClick={() => setLangOpen((o) => !o)}
              className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-800 hover:border-background-300 cursor-pointer whitespace-nowrap"
              aria-haspopup="listbox"
              aria-expanded={langOpen}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-global-line text-base" />
              </span>
              <span>{LANG_LABELS[lang as keyof typeof LANG_LABELS] ?? 'English'}</span>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-sm" />
              </span>
            </button>

            {langOpen && (
              <div
                role="listbox"
                className="absolute right-0 top-full mt-2 w-40 rounded-lg border border-background-200 bg-background-50 shadow-sm p-1"
              >
                {SUPPORTED_LANGS.map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => switchLang(l)}
                    className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm cursor-pointer transition-colors whitespace-nowrap ${
                      l === lang
                        ? 'bg-primary-100/70 text-primary-900 font-semibold'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    <span>{LANG_LABELS[l]}</span>
                    {l === lang && (
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-check-line text-sm" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 px-4 py-2 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('nav.cta')}
          </a>

          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-900 cursor-pointer"
            aria-label="Menu"
          >
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 max-h-[80vh] overflow-y-auto">
          <div className="space-y-1 mb-4">
            <Link
              to={`/${lang}`}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-6 h-6 flex items-center justify-center text-primary-600">
                <i className="ri-home-4-line text-base" />
              </span>
              {t('nav.home')}
            </Link>
            <Link
              to={`/${lang}/guide/basetao-buying-guide`}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-6 h-6 flex items-center justify-center text-primary-600">
                <i className="ri-book-open-line text-base" />
              </span>
              {t('nav.guide')}
            </Link>
            <ScrollLink to="categories" onClick={() => setMobileOpen(false)}>
              {t('nav.products')}
            </ScrollLink>
            <ScrollLink to="topics" onClick={() => setMobileOpen(false)}>
              {t('nav.guides')}
            </ScrollLink>
            <ScrollLink to="faq" onClick={() => setMobileOpen(false)}>
              {t('nav.safety')}
            </ScrollLink>
          </div>

          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 px-3 mb-2">
            {t('categories.title')}
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {categories.map((c) => (
              <a
                key={c.key}
                href={c.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-100/70 hover:text-primary-900 transition-colors cursor-pointer"
              >
                <span className="w-6 h-6 flex items-center justify-center text-primary-600 shrink-0">
                  <i className={`${c.icon} text-base`} />
                </span>
                <span className="truncate">{t(c.key)}</span>
              </a>
            ))}
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="block w-full text-center rounded-md bg-primary-500 text-background-50 px-4 py-2.5 text-sm font-semibold cursor-pointer whitespace-nowrap mt-4"
          >
            {t('nav.cta')}
          </a>
        </div>
      )}
    </header>
  );
}